<script setup lang="ts">
import { formatMoney, ratioProportion } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { drawnState } from '../internal/drawn.ts'
import type { Loan } from '../internal/types.ts'

const props = defineProps<{ loan: Loan; hasHistory: boolean | undefined }>()
const { locale, t } = useI18n()

const drawn = computed(() => drawnState(props.loan, props.hasHistory))
const principalRepaid = computed(() => {
  const value = props.loan.progress.principalRepaid
  return value === undefined ? undefined : ratioProportion(value)
})
const termElapsed = computed(() => {
  const value = props.loan.progress.termElapsed
  return props.loan.endDate === undefined || value === undefined ? undefined : ratioProportion(value)
})
const remaining = computed(() =>
  t('loans.remaining', {
    amount: formatMoney(props.loan.outstandingPrincipal, { locale: locale.value as 'en' | 'hu' }),
  }),
)
const termText = computed(() =>
  t('loans.termElapsed', { percent: Math.round((termElapsed.value ?? 0) * 100) }),
)
</script>

<template>
  <p
    v-if="drawn === 'not-drawn'"
    class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
  >
    {{ t('loans.notYetDrawn') }}
  </p>
  <div
    v-else-if="drawn === 'drawn' && principalRepaid !== undefined"
    class="flex min-w-0 flex-col gap-[var(--space-tight)]"
  >
    <div aria-hidden="true" class="h-2.5 overflow-hidden rounded-full bg-[color:var(--color-surface-sunken)]">
      <div
        class="h-full rounded-full bg-[color:var(--color-accent)]"
        :style="{ width: `${principalRepaid * 100}%` }"
      />
    </div>
    <p class="truncate text-[length:var(--text-step-1)] tabular-nums text-[color:var(--color-text-muted)]">
      {{ remaining }}
    </p>
    <template v-if="termElapsed !== undefined">
      <div aria-hidden="true" class="h-1 overflow-hidden rounded-full bg-[color:var(--color-surface-sunken)]">
        <div
          class="h-full rounded-full bg-[color:var(--color-text-muted)]"
          :style="{ width: `${termElapsed * 100}%` }"
        />
      </div>
      <p class="truncate text-[length:var(--text-step-1)] tabular-nums text-[color:var(--color-text-muted)]">
        {{ termText }}
      </p>
    </template>
  </div>
</template>
